import { type ReactNode, useState } from 'react'

import { DataTable, FullScreenContainer } from '@/components/ui'
import type { ColumnDef, Table } from '@/components/ui/table/types'
import {
  type OutlierTransaction,
  type Transaction,
  TransactionsBulkActions,
  transactionsColumns,
} from '@/modules/projects'
import { transactionFilterSchema } from '@/schemas/transactionFilterSchema'
import { FiltersProvider, useFilters, useSearchQuery } from '@/stores'
import { cn } from '@/utils'
import { Section } from '../shared'
import { SectionToolbar } from './SectionToolbar'

interface TransactionsAnomalySectionProps {
  slug: string
  id: string
  title: string
  description: ReactNode
  emptyMessage: string
  filter: (transactions: Transaction[]) => Transaction[]
  transactions: Transaction[]
  columnsAfterAmount?: ColumnDef<OutlierTransaction, unknown>[]
}

export function TransactionsAnomalySection(
  props: TransactionsAnomalySectionProps,
) {
  return (
    <FiltersProvider contextId={`${props.slug}-anomalies-${props.id}`}>
      <TransactionsAnomalySectionContent {...props} />
    </FiltersProvider>
  )
}

function TransactionsAnomalySectionContent({
  slug,
  id,
  title,
  description,
  emptyMessage,
  filter,
  transactions,
  columnsAfterAmount = [],
}: TransactionsAnomalySectionProps) {
  const [table, setTable] = useState<Table<Transaction> | null>(null)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const filters = useFilters()
  const searchQuery = useSearchQuery()

  const flaggedTransactions = [...filter(transactions)].sort(
    (first, second) => Math.abs(second.amount) - Math.abs(first.amount),
  )
  const hasShareNumber = flaggedTransactions.some(
    (transaction) => transaction.shareNumber,
  )
  const columns = transactionsColumns(hasShareNumber).flatMap((column) =>
    'accessorKey' in column && column.accessorKey === 'amount'
      ? [column, ...columnsAfterAmount]
      : [column],
  )

  return (
    <FullScreenContainer isActive={isFullScreen}>
      <Section
        title={title}
        description={description}
        count={flaggedTransactions.length}
        className={cn(isFullScreen && 'flex min-h-0 flex-1 flex-col')}
      >
        <SectionToolbar
          dialogId={`anomalies-${id}`}
          exportFilename={`${slug}-${id}-anomalies`}
          table={table}
          hasShareNumber={hasShareNumber}
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
        />
        <DataTable
          columns={columns}
          data={flaggedTransactions}
          height={isFullScreen ? 'flex-1 min-h-0' : 'h-auto'}
          emptyMessage={emptyMessage}
          onTableReady={setTable}
          filters={filters}
          filterSchema={transactionFilterSchema}
          globalFilter={searchQuery}
          pageSizes={[25, 50, 100]}
          initialPageSize={25}
          bulkActions={(selection) => (
            <TransactionsBulkActions
              selection={selection}
              transactions={flaggedTransactions}
            />
          )}
        />
      </Section>
    </FullScreenContainer>
  )
}
